/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the "Elastic License
 * 2.0", the "GNU Affero General Public License v3.0 only", and the "Server Side
 * Public License v 1"; you may not use this file except in compliance with, at
 * your election, the "Elastic License 2.0", the "GNU Affero General Public
 * License v3.0 only", or the "Server Side Public License, v 1".
 */

import React, { useCallback, useEffect, useState } from 'react'

import { useConfig } from '@modules/cui/ConfigContext'
import { useLocalStorage } from '@modules/utils/hooks/useLocalStorage'

import FeedbackPanel from './FeedbackPanel'
import { useEbtFeedbackSnippet } from './useEbtFeedbackSnippet'

import type { FeedbackSnippetIds } from './FeedbackSnippetIds'
import type { FunctionComponent } from 'react'
import type { FeedbackType, FeedbackView } from './types'

const FEEDBACK_PANEL_POSITIVE_LIFETIME = 3000

interface FeedbackSnippetProps {
  /**
   * Id used for local storage, EBT and for HTML ids.
   */
  feedbackSnippetId: FeedbackSnippetIds
  /**
   * Message to display during the feedback prompt view.
   */
  promptViewMessage: React.ReactNode
}

/**
 * A snippet to gather user feedback.
 */
const FeedbackSnippet: FunctionComponent<FeedbackSnippetProps> = ({
  feedbackSnippetId,
  promptViewMessage,
}) => {
  const isUserConsole = useConfig('APP_NAME') === 'userconsole'
  const [feedbackView, setFeedbackView] = useState<FeedbackView>('prompt')
  const [feedbackInteraction, setFeedbackInteraction] = useLocalStorage(feedbackSnippetId)
  const [showPanel, setShowPanel] = useState(() => feedbackInteraction === null)
  const { reportFeedbackSnippetEbt } = useEbtFeedbackSnippet()

  const storeFeedbackInteraction = useCallback(() => {
    setFeedbackInteraction(Date.now().toString())
  }, [setFeedbackInteraction])

  const handleDismissPanel = useCallback(() => {
    setShowPanel(false)
    storeFeedbackInteraction()
  }, [storeFeedbackInteraction])

  const handleFeedback = (feedback: FeedbackType) => {
    setFeedbackView(feedback)
    storeFeedbackInteraction()

    // Track feedback with EBT
    reportFeedbackSnippetEbt(feedbackSnippetId, feedback)
  }

  useEffect(() => {
    let timer: number | undefined

    if (feedbackView !== 'prompt') {
      timer = window.setTimeout(() => {
        handleDismissPanel()
      }, FEEDBACK_PANEL_POSITIVE_LIFETIME)
    }

    return () => {
      if (timer) {
        window.clearTimeout(timer)
      }
    }
  }, [feedbackView, handleDismissPanel])

  if (!isUserConsole || !showPanel) {
    return null
  }

  return (
    <FeedbackPanel
      feedbackSnippetId={feedbackSnippetId}
      feedbackView={feedbackView}
      onDismissPanel={handleDismissPanel}
      onFeedback={handleFeedback}
      promptViewMessage={promptViewMessage}
    />
  )
}

export default FeedbackSnippet
